import type { TimeGap } from "@/hooks/use-time-gaps";
import { formatHours } from "@/lib/format";

/**
 * VIB-2083: oppsummering av hull i måneden, vist før den ansatte avslutter.
 *
 * Daglista farger allerede hver enkelt dag, men en ansatt som scroller forbi
 * 30 rader ser ikke nødvendigvis at tre av dem er tomme. Denne gir tallet
 * eksplisitt rett over Avslutt-knappen.
 *
 * Beslutning (Erik 28.09): dette VARSLER, det blokkerer ikke. Knappen fungerer
 * uansett — poenget er at ingen avslutter måneden uten å ha sett hullene.
 */
export interface MissingDaysSummary {
  /** Ukedager i måneden uten en eneste registrert time. */
  missingDays: number;
  /** Ukedager som har timer, men færre enn forventet. */
  partialDays: number;
  /** Timer som mangler på de delvis førte dagene, avrundet til én desimal. */
  partialGapHours: number;
}

/**
 * Tell hull blant dagene som faktisk KAN ha timer.
 *
 * Utelatt fra tellingen:
 *  - helger (`isWeekend`) — ingen forventning om timer
 *  - fraværsdager (`isAbsent`) — ferie/sykdom er gjort rede for
 *  - framtidige dager (`isFuture`) — lukker du oktober 3. oktober, er resten
 *    av måneden ikke et hull ennå
 *  - dager uten forventede timer (`expectedHours <= 0`) — helligdager
 *    (VIB-3046), halv fraværsdag o.l. der kapasiteten allerede er trukket fra
 */
export function summarizeMissingDays(gaps: TimeGap[]): MissingDaysSummary {
  let missingDays = 0;
  let partialDays = 0;
  let partialGapHours = 0;

  for (const g of gaps) {
    if (g.isWeekend || g.isAbsent || g.isFuture) continue;
    if (g.expectedHours <= 0) continue;

    if (g.registeredHours <= 0) {
      missingDays += 1;
    } else if (g.gap > 0) {
      partialDays += 1;
      partialGapHours += g.gap;
    }
  }

  return {
    missingDays,
    partialDays,
    partialGapHours: Math.round(partialGapHours * 10) / 10,
  };
}

/** Har måneden noe å varsle om i det hele tatt? */
export function hasGaps(s: MissingDaysSummary): boolean {
  return s.missingDays > 0 || s.partialDays > 0;
}

/**
 * Hullene i én setningsdel, f.eks. «3 ukedager uten timer og 2 dager med færre
 * timer enn forventet (4t til sammen)». Brukt øverst og ved Lukk-knappen.
 */
export function describeGaps(s: MissingDaysSummary): string {
  const parts: string[] = [];
  if (s.missingDays > 0) {
    parts.push(s.missingDays === 1 ? "1 ukedag uten timer" : `${s.missingDays} ukedager uten timer`);
  }
  if (s.partialDays > 0) {
    parts.push(
      `${s.partialDays} ${s.partialDays === 1 ? "dag" : "dager"} med færre timer enn forventet (${formatHours(s.partialGapHours)} til sammen)`,
    );
  }
  return parts.join(" og ");
}
